import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Printer, ArrowLeft, Download } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { getHBLByID } from '@/redux/Booking/actions';
import ReceivedStamp from '../ui/stamp';
import { buildHblPrintDocumentHtml } from "./hblprintHTML";
import { baseURL } from '@/config/APIConfig';
import { axiosInstance } from '@/config/NetworkUtility';

// ─────────────────────────────────────────────────────────────────────────────

const HBLPrint = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { bookingId: bookingIdFromState, FILENUMBER } = location.state || {};
  const searchParams = new URLSearchParams(location.search);
  const bookingIdFromQuery = searchParams.get('bookingId');
  const bookingIdParsed =
    bookingIdFromQuery != null && bookingIdFromQuery !== ''
      ? Number(bookingIdFromQuery) || bookingIdFromQuery
      : null;
  const bookingId =
    bookingIdFromState != null && bookingIdFromState !== ''
      ? bookingIdFromState
      : bookingIdParsed;

  const { MasterCompanyDetails } = useSelector((state) => state.common);
  const { id: idQuote } = useParams();

  const [formData, setFormData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Three refs — one per logical print region (same pattern as BookingPrint).
  // The browser sees these as normal DOM refs; we extract their outerHTML when
  // building the print shell.
  const headerRef = useRef();   // .hbl-print-header-block
  const bodyRef = useRef();     // main content below the header
  const footerRef = useRef();   // powered-by disclaimer

  // Also keep a ref for the whole document (used by legacy handlePrint path
  // which relies on the iframe approach for browser printing).
  const documentRef = useRef();

  const [arCharges, setArCharges] = useState([]);
  const [apCharges, setApCharges] = useState([]);

  // ── Data loading ─────────────────────────────────────────────────────────

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      try {
        if (bookingId && bookingId !== 0) {
          await _getBooking();
        }
      } catch (error) {
        console.error('Error initializing booking:', error);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [bookingId]);

  const _getBooking = async () => {
    try {
      if (
        typeof bookingId === 'number' ||
        (typeof bookingId === 'string' && bookingId !== '0')
      ) {
        const response = await getHBLByID(bookingId);
        if (response?.data) {
          const mappedData = {
            ...response.data,
            ContainersCargoModel: (response.data.ContainersCargoModel || []).map((cargo) => ({
              MARKS_NO: cargo.marksAndNumbers || cargo.MARKS_NO || '',
              CONTAINER_NUMBER: cargo.containerNo || cargo.CONTAINER_NUMBER || '',
              SEAL_NUMBER: cargo.sealNo || cargo.SEAL_NUMBER || '',
              CONT_SIZE: cargo.containerSize || cargo.CONT_SIZE || '',
              CONT_TYPE: cargo.containerType || cargo.CONT_TYPE || '',
              DESCRIPTIONS: cargo.description || cargo.DESCRIPTIONS || '',
              SCHEDULE_B: cargo.scheduleB || cargo.SCHEDULE_B || '',
              NO_PACKAGE: cargo.numberOfPackages || cargo.NO_PACKAGE || 0,
              PACKAGE_CODE: cargo.packageCode || cargo.PACKAGE_CODE || '',
              PACKAGE_DESCRIPTION: cargo.packageDescription || cargo.PACKAGE_DESCRIPTION || '',
              HAZ_CODE: cargo.hazCode || cargo.HAZ_CODE || '',
              KGS: cargo.weightKg || cargo.KGS || '',
              LBS: cargo.weightLb || cargo.LBS || '',
              CBM: cargo.measurementCbm || cargo.CBM || '',
            })),
          };
          setArCharges(response?.data?.hblArCharges || []);
          setFormData(mappedData);
        }
      } else if (bookingId && typeof bookingId === 'object') {
        setFormData(bookingId);
      }
    } catch (error) {
      console.error('Error fetching booking:', error);
    }
  };

  // ── Charges helpers (unchanged from original) ─────────────────────────────

  const normalizeCharges = (ar, ap) => [
    ...(ar?.map((c) => ({
      chargeCode: c.chargeCode,
      description: c.description,
      ppcc: c.ppcc,
      amount: Number(c.sellRate || 0),
    })) ?? []),
    ...(ap?.map((c) => ({
      chargeCode: c.chargeCode,
      description: c.description,
      ppcc: c.ppcc,
      amount: Number(c.buyRate || 0),
    })) ?? []),
  ];

  const groupCharges = (charges) =>
    Object.values(
      charges.reduce((acc, item) => {
        const key = item.chargeCode?.trim();
        if (!acc[key]) {
          acc[key] = {
            chargeCode: item.chargeCode,
            description: item.description,
            prepaid: 0,
            collect: 0,
            total: 0,
          };
        }
        if (item.ppcc === 'P') acc[key].prepaid += item.amount;
        else if (item.ppcc === 'C') acc[key].collect += item.amount;
        acc[key].total += item.amount;
        return acc;
      }, {}),
    );

  const [groupedCharges, setGroupedCharges] = useState([]);

  useEffect(() => {
    setGroupedCharges(groupCharges(normalizeCharges(arCharges, apCharges)));
  }, [arCharges, apCharges]);

  const formatDate = (d) =>
    d && moment(d).isValid() ? moment(d).format('DD MMM YYYY') : '';

  // ─────────────────────────────────────────────────────────────────────────
  // SHARED IMAGE UTILITIES (IMPROVED FOR RELIABLE LOGO RENDERING)
  // ─────────────────────────────────────────────────────────────────────────

  const waitImagesLoaded = async (root) => {
    const images = root.querySelectorAll('img');
    await Promise.all(
      Array.from(images).map((img) => {
        if (img.complete) return Promise.resolve();
        return new Promise((resolve) => {
          img.onload = resolve;
          img.onerror = resolve;
          setTimeout(resolve, 2000);
        });
      }),
    );
  };

  const blobToDataUrl = (blob) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });

  const resolveImageRequestUrl = (src) => {
    if (!src || src.startsWith('data:')) return src;
    const t = String(src).trim();
    if (/^https?:\/\//i.test(t)) return t;
    if (t.startsWith('//')) return `${window.location.protocol}${t}`;
    if (t.startsWith('/')) {
      try { return new URL(t, window.location.href).href; } catch { /* noop */ }
    }
    const base = baseURL.live.replace(/\/?$/, '/');
    const path = t.startsWith('/') ? t.slice(1) : t;
    return `${base}${path}`;
  };

  // 🔧 IMPROVED: Reliable image fetching with auth & CORS handling
  const fetchImageAsDataUrl = async (src) => {
    if (!src || src.startsWith('data:')) return src || null;

    const absoluteUrl = resolveImageRequestUrl(src);
    const token = localStorage.getItem('authToken');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    // Try native fetch first (better CORS handling)
    try {
      const res = await fetch(absoluteUrl, {
        method: 'GET',
        headers,
        credentials: 'include',
        mode: 'cors',
      });
      if (res.ok) {
        const blob = await res.blob();
        if (blob.size > 0) return await blobToDataUrl(blob);
      }
    } catch (e) {
      console.warn('Fetch failed, trying axios', e);
    }

    // Fallback to axios (for authenticated endpoints)
    try {
      const { data } = await axiosInstance({
        method: 'get',
        url: absoluteUrl,
        responseType: 'blob',
        headers,
      });
      if (data && data.size > 0) return await blobToDataUrl(data);
    } catch (e) {
      console.error('Axios image fetch failed', e);
    }
    return null;
  };

  const tryDrawLiveImageToDataUrl = (liveImg) => {
    if (!liveImg || !liveImg.complete || !(liveImg.naturalWidth > 0)) return null;
    try {
      const c = document.createElement('canvas');
      c.width = liveImg.naturalWidth;
      c.height = liveImg.naturalHeight;
      c.getContext('2d').drawImage(liveImg, 0, 0);
      return c.toDataURL('image/png');
    } catch { return null; }
  };

  // 🔧 IMPROVED: Sequential image inlining with decode waiting
  const inlineImagesForHblPdf = async (cloneRoot, liveRoot) => {
    const cloneImgs = cloneRoot.querySelectorAll('img');
    const liveImgs = liveRoot.querySelectorAll('img');

    for (let i = 0; i < cloneImgs.length; i++) {
      const cImg = cloneImgs[i];
      const liveImg = liveImgs[i];
      let src =
        (liveImg && (liveImg.currentSrc || liveImg.getAttribute('src'))) ||
        cImg.getAttribute('src') ||
        '';
      if (!src || src.startsWith('data:')) continue;

      let dataUrl = tryDrawLiveImageToDataUrl(liveImg);
      if (!dataUrl) dataUrl = await fetchImageAsDataUrl(src);

      if (dataUrl && String(dataUrl).startsWith('data:')) {
        cImg.setAttribute('src', dataUrl);
        // Wait for the image to actually decode
        await new Promise((resolve) => {
          if (cImg.complete) resolve();
          else {
            cImg.onload = resolve;
            cImg.onerror = resolve;
            setTimeout(resolve, 2000);
          }
        });
      }
    }
  };

  const scaleCanvasToWidth = (source, targetWidth) => {
    if (!source || source.width === targetWidth) return source;
    const tw = Math.max(1, Math.round(targetWidth));
    const th = Math.max(1, Math.round(source.height * (tw / source.width)));
    const c = document.createElement('canvas');
    c.width = tw;
    c.height = th;
    const ctx = c.getContext('2d');
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(source, 0, 0, source.width, source.height, 0, 0, tw, th);
    return c;
  };

  // ─────────────────────────────────────────────────────────────────────────
  // PDF LAYER CAPTURE — with increased timing stability
  // ─────────────────────────────────────────────────────────────────────────

  const captureHblPdfLayersFromPrintShell = async (
    headerHtml,
    bodyHtml,
    footerHtml,
    documentTitle,
  ) => {
    const iframe = document.createElement('iframe');
    iframe.setAttribute('aria-hidden', 'true');
    iframe.style.cssText =
      'position:fixed;left:-12000px;top:0;width:210mm;min-width:794px;' +
      'height:12000px;min-height:12000px;border:0;opacity:0;pointer-events:none;overflow:auto';
    document.body.appendChild(iframe);

    try {
      const idoc = iframe.contentDocument;
      const win = idoc.defaultView;

      idoc.open();
      idoc.write(buildHblPrintDocumentHtml(headerHtml, bodyHtml, footerHtml, documentTitle));
      idoc.close();

      // 🕒 Increased wait time for images and layout
      await new Promise((r) => setTimeout(r, 1200));
      await waitImagesLoaded(idoc.body);

      // Force two animation frames to allow layout to settle
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
      await new Promise((r) => setTimeout(r, 300)); // extra settle

      const headerCell = idoc.querySelector('.hbl-print-header-cell');
      const bodyCell = idoc.querySelector('.hbl-print-body-cell');
      const footerCell = idoc.querySelector('.hbl-print-footer-cell');

      if (!headerCell || !bodyCell || !footerCell) {
        throw new Error('HBL print layout regions not found in iframe');
      }

      const bodyRect = bodyCell.getBoundingClientRect();
      const cargoTheadEl = idoc.querySelector('.hbl-cargo-table thead');
      const cargoTableEl = idoc.querySelector('.hbl-cargo-table');

      const scrollH = Math.max(
        idoc.documentElement?.scrollHeight || 0,
        idoc.body?.scrollHeight || 0,
        win?.innerHeight || 0,
      );

      const canvasOpts = {
        scale: 3,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        windowWidth: win.innerWidth,
        windowHeight: scrollH,
      };

      const headerCanvas = await html2canvas(headerCell, canvasOpts);
      const bodyCanvas = await html2canvas(bodyCell, canvasOpts);
      const footerCanvas = await html2canvas(footerCell, canvasOpts);

      let cargoTheadCanvas = null;
      let cargoTheadMeta = null;

      if (cargoTheadEl && cargoTableEl && bodyRect.height > 0) {
        const theadRect = cargoTheadEl.getBoundingClientRect();
        const tableRect = cargoTableEl.getBoundingClientRect();
        const pxScale = bodyCanvas.height / bodyRect.height;

        cargoTheadCanvas = await html2canvas(cargoTheadEl, canvasOpts);
        cargoTheadMeta = {
          topPx: (theadRect.top - bodyRect.top) * pxScale,
          bottomPx: (theadRect.bottom - bodyRect.top) * pxScale,
          tableBottomPx: (tableRect.bottom - bodyRect.top) * pxScale,
        };
      }

      const refW = bodyCanvas.width;
      return {
        headerCanvas: scaleCanvasToWidth(headerCanvas, refW),
        bodyCanvas,
        footerCanvas: scaleCanvasToWidth(footerCanvas, refW),
        cargoTheadCanvas: cargoTheadCanvas
          ? scaleCanvasToWidth(cargoTheadCanvas, refW)
          : null,
        cargoTheadMeta,
      };
    } finally {
      iframe.remove();
    }
  };

  // ── Download PDF (client-side canvas approach) ───────────────────────────

  const handleDownload = async () => {
    if (!headerRef.current || !bodyRef.current || !footerRef.current || !formData) return;

    const downloadBtn = document.querySelector('[data-download-btn]');
    if (downloadBtn) {
      downloadBtn.textContent = 'Generating PDF…';
      downloadBtn.disabled = true;
    }

    try {
      const headerClone = headerRef.current.cloneNode(true);
      const bodyClone = bodyRef.current.cloneNode(true);
      const footerClone = footerRef.current.cloneNode(true);

      await inlineImagesForHblPdf(headerClone, headerRef.current);
      await inlineImagesForHblPdf(bodyClone, bodyRef.current);
      await inlineImagesForHblPdf(footerClone, footerRef.current);

      const { headerCanvas, bodyCanvas, footerCanvas, cargoTheadCanvas, cargoTheadMeta } =
        await captureHblPdfLayersFromPrintShell(
          headerClone.outerHTML,
          bodyClone.outerHTML,
          footerClone.outerHTML,
          `HBL_${formData.HBLNumber || 'Document'}`,
        );

      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const margin = 8;
      const mmWidth = pdfWidth - margin * 2;

      const toMm = (canvas) => canvas.height * (mmWidth / canvas.width);
      const headerMm = toMm(headerCanvas);
      const footerMm = toMm(footerCanvas);
      const bodyTotalMm = toMm(bodyCanvas);
      const cargoTheadMm = cargoTheadCanvas ? toMm(cargoTheadCanvas) : 0;
      const cargoTheadData = cargoTheadCanvas
        ? cargoTheadCanvas.toDataURL('image/png')
        : null;

      const yFooter = pdfHeight - margin - footerMm + 0.5;
      const yBodyStart = margin + headerMm;
      const availableBodyMm = Math.max(1, yFooter - yBodyStart);
      const pxPerMmBody = bodyCanvas.height / bodyTotalMm;

      const headerData = headerCanvas.toDataURL('image/png');
      const footerData = footerCanvas.toDataURL('image/png');

      let yPx = 0;
      let pageIndex = 0;
      const bodyH = bodyCanvas.height;

      while (yPx < bodyH - 0.25) {
        if (pageIndex > 0) pdf.addPage();

        const needsCargoHeader =
          cargoTheadData &&
          cargoTheadMeta &&
          yPx > cargoTheadMeta.bottomPx + 1 &&
          yPx < cargoTheadMeta.tableBottomPx - 1;

        const cargoHeaderReserveMm = needsCargoHeader ? cargoTheadMm : 0;
        const effectiveBodyStart = yBodyStart + cargoHeaderReserveMm;
        const pageAvailableBodyMm = Math.max(1, yFooter - effectiveBodyStart);

        const remainingPx = bodyH - yPx;
        const maxSlicePx = pageAvailableBodyMm * pxPerMmBody;
        const slicePx = Math.min(maxSlicePx, remainingPx);

        const sliceCanvas = document.createElement('canvas');
        sliceCanvas.width = bodyCanvas.width;
        sliceCanvas.height = slicePx;
        sliceCanvas
          .getContext('2d')
          .drawImage(bodyCanvas, 0, yPx, bodyCanvas.width, slicePx, 0, 0, bodyCanvas.width, slicePx);
        const sliceData = sliceCanvas.toDataURL('image/png');
        const sliceMm = slicePx * (mmWidth / bodyCanvas.width);

        pdf.addImage(headerData, 'PNG', margin, margin, mmWidth, headerMm);
        if (needsCargoHeader) {
          pdf.addImage(cargoTheadData, 'PNG', margin, yBodyStart, mmWidth, cargoTheadMm);
        }
        pdf.addImage(sliceData, 'PNG', margin, effectiveBodyStart, mmWidth, sliceMm);
        pdf.addImage(footerData, 'PNG', margin, yFooter, mmWidth, footerMm);

        yPx += slicePx;
        pageIndex++;
      }

      pdf.save(
        `HOUSE_BILL_OF_LADING_${formData.HBLNumber || 'Document'}_${moment().format('YYYY-MM-DD')}.pdf`,
      );
    } catch (error) {
      console.error('Error generating HBL PDF:', error);
      alert('Failed to generate PDF. Please try printing instead.');
    } finally {
      if (downloadBtn) {
        downloadBtn.textContent = 'Download PDF';
        downloadBtn.disabled = false;
      }
    }
  };

  // ── Browser print (unchanged, uses same HTML shell) ───────────────────────

  const handlePrint = () => {
    if (!headerRef.current || !bodyRef.current || !footerRef.current) return;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(
      buildHblPrintDocumentHtml(
        headerRef.current.outerHTML,
        bodyRef.current.outerHTML,
        footerRef.current.outerHTML,
        `HBL - ${formData?.HBLNumber || 'Document'}`,
      ),
    );
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.onafterprint = () => printWindow.close();
    }, 900);
  };

  // ── Loading / error states ───────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-foreground">Loading house bill of lading data...</p>
        </div>
      </div>
    );
  }

  if (!formData) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-red-600 mb-4">Failed to load house bill of lading data</p>
          <button
            onClick={() =>
              navigate('/hbl', { state: { bookingId: bookingId, FILENUMBER: FILENUMBER } })
            }
            className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to HBL
          </button>
        </div>
      </div>
    );
  }

  const company = MasterCompanyDetails?.[0] || {};

  // ── Render (identical to original) ───────────────────────────────────────

  return (
    <div className="min-h-screen bg-gray-200 print:bg-white text-gray-900">

      {/* Action bar (screen-only) */}
      <div className="print:hidden bg-white shadow-sm border-b border-gray-300 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-3 sm:px-4 py-2 sm:py-3 flex flex-col sm:flex-row items-center gap-2 sm:gap-0 sm:justify-between">
          <button
            onClick={() =>
              navigate('/hbl', { state: { bookingId: bookingId, FILENUMBER: FILENUMBER } })
            }
            className="w-full sm:w-auto flex items-center justify-center sm:justify-start gap-2 px-4 py-2 text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to HBL
          </button>
          <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto">
            <button
              onClick={handleDownload}
              data-download-btn
              className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-3 sm:px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-all font-medium border border-gray-300 text-sm sm:text-base"
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">Download PDF</span>
              <span className="sm:hidden">Download</span>
            </button>
            <button
              onClick={handlePrint}
              className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 sm:px-5 py-2 bg-[#1a2744] hover:bg-[#0f172a] text-white rounded-lg transition-all font-medium text-sm sm:text-base"
            >
              <Printer className="w-4 h-4" />
              Print
            </button>
          </div>
        </div>
      </div>

      {/* A4 document preview */}
      <div className="hbl-print-stage mx-auto px-2 sm:px-4 py-4 sm:py-6">
        <div
          ref={documentRef}
          className="hbl-print-doc bg-white shadow-lg print:shadow-none mx-auto"
          style={{
            fontFamily: 'Arial, sans-serif',
            width: '210mm',
            minHeight: '297mm',
            padding: '8mm',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
          }}
        >
          <style>{`
            /* ─── Scoped to the on-screen preview ──────────────── */
            .hbl-print-stage { overflow-x: auto; }

            .bol-value {
              color: #000 !important;
              font-weight: 700 !important;
              font-size: 10px !important;
              letter-spacing: 0.02em;
              text-transform: uppercase;
              line-height: 1.3;
            }

            .hbl-paragraph,
            .hbl-paragraph * {
              text-transform: none !important;
              font-weight: 400 !important;
              letter-spacing: normal !important;
            }

            .hbl-print-doc .text-xs {
              color: #1f2937;
              font-size: 11px;
              line-height: 1.3;
            }

            .hbl-charges-table {
              width: 100%;
              border-collapse: collapse;
              table-layout: fixed;
              margin: 0;
            }
            .hbl-charges-table th,
            .hbl-charges-table td {
              border: 1px solid #000;
              padding: 3px 6px;
              vertical-align: middle;
              line-height: 1.25;
              text-align: center;
            }
            .hbl-charges-table thead th {
              background-color: #d9d9d9;
              color: #000;
              font-weight: 800;
              text-transform: uppercase;
              letter-spacing: 0.03em;
              font-size: 9px;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            .hbl-charges-table tbody td {
              color: #1f2937;
              font-size: 10px;
            }
            .hbl-charges-table tbody td:first-child { text-align: left; }
            .hbl-charges-table tfoot td {
              background-color: #f3f3f3;
              color: #000;
              font-weight: 800;
              font-size: 11px;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            .hbl-charges-table tfoot td:first-child {
              text-align: right;
              text-transform: uppercase;
              letter-spacing: 0.02em;
            }

            .hbl-cargo-table {
              width: 100%;
              border-collapse: collapse;
              table-layout: fixed;
            }
            .hbl-cargo-table thead {
              display: table-header-group;
            }
            .hbl-cargo-table thead th {
              border-bottom: 1px solid #000;
              border-right: 1px solid #000;
              padding: 4px;
              text-align: center;
              vertical-align: middle;
            }
            .hbl-cargo-table thead th:last-child {
              border-right: none;
            }
            .hbl-cargo-table tbody td {
              border-right: 1px dashed #000;
              padding: 4px;
              vertical-align: top;
            }
            .hbl-cargo-table tbody td:last-child {
              border-right: none;
            }
            .hbl-cargo-table tbody tr {
              page-break-inside: auto;
              break-inside: auto;
            }

            /* Page-break guards (browser print path) */
            .hbl-bottom-section     { page-break-inside: avoid; break-inside: avoid; }
            .hbl-signature-block    { page-break-inside: avoid; break-inside: avoid; }
            .hbl-footer-row         { page-break-inside: avoid; break-inside: avoid; }

            .watermark { display: none !important; }

            @media print {
              @page { size: A4; margin: 0; }
              html, body {
                margin: 0 !important; padding: 0 !important;
                background: #fff !important;
              }
              .hbl-print-doc {
                width: 210mm !important; min-height: 297mm !important;
                padding: 8mm !important; margin: 0 !important;
                box-shadow: none !important; background: #fff !important;
                box-sizing: border-box !important;
                display: flex !important; flex-direction: column !important;
              }
              .hbl-print-doc,
              .hbl-print-doc *,
              .hbl-print-doc *::before,
              .hbl-print-doc *::after {
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
                color-adjust: exact !important;
              }
              .hbl-print-doc .border,
              .hbl-print-doc .border-t,
              .hbl-print-doc .border-b,
              .hbl-print-doc .border-l,
              .hbl-print-doc .border-r {
                border-style: solid !important;
              }
              .hbl-print-doc .border-black {
                border-color: #000 !important;
              }
              .hbl-charges-table thead th,
              .hbl-charges-table tfoot td {
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
              }
            }
          `}</style>

          {/* HEADER REGION (repeats on every page) */}
          <div
            ref={headerRef}
            className="hbl-print-header-block flex flex-col sm:flex-row justify-between items-start mb-2 gap-2"
            style={{ paddingBottom: '6px' }}
          >
            <div className="flex items-center">
              <div>
                {MasterCompanyDetails?.[0]?.CompanyId === 3 ? (
                  <img
                    src="https://api.shipez.ai/UploadedFile/CompanyProfile/AtlanticPacificLines/apllogo.png"
                    alt={MasterCompanyDetails?.[0]?.CompanyName || "Company Logo"}
                    className="h-10 sm:h-12 w-auto"
                  />
                ) : (
                  <img
                    src={MasterCompanyDetails?.[0]?.CompanyLogoDark || ""}
                    alt={MasterCompanyDetails?.[0]?.CompanyName || "Company Logo"}
                    className="h-10 sm:h-12 w-auto"
                  />
                )}
              </div>
            </div>
            <h1 className="text-2xl sm:text-4xl font-bold text-primary tracking-wide">
              BILL OF LADING
            </h1>
          </div>

          {/* BODY REGION (paginated content) */}
          <div ref={bodyRef} className="hbl-print-body-content" style={{ flex: 1 }}>
            {/* Main bordered grid */}
            <div className="border border-black hbl-main-grid">

              {/* Row 1: Shipper / Booking No + B/L No */}
              <div className="grid grid-cols-12">
                <div className="col-span-6 border-r border-black">
                  <div className="p-1 min-h-[70px]">
                    <div className="bol-value">SHIPPER/ EXPORTER (COMPLETE NAME AND ADDRESS)</div>
                    <div className="text-xs whitespace-pre-line">
                      {formData?.ShipperAddress
                        ? formData.ShipperAddress
                        : company?.ProfileAddressPrint?.split('\r\n').map((line, i) => (
                            <div key={i}>{line}</div>
                          ))}
                    </div>
                  </div>
                </div>
                <div className="col-span-6">
                  <div className="grid grid-cols-6 border-b border-black">
                    <div className="col-span-3 p-1 border-r border-black">
                      <div className="bol-value">BOOKING NUMBER</div>
                      <div className="text-xs">{formData?.CarrierBookingNo || ''}</div>
                    </div>
                    <div className="col-span-3 p-1">
                      <div className="bol-value">B/L NUMBER</div>
                      <div className="text-xs">{formData?.HBLNumber || ' '}</div>
                    </div>
                  </div>
                  <div className="p-1">
                    <div className="bol-value">EXPORT REFERENCES</div>
                    <div className="text-xs">
                      {formData?.ExportRefNumber || ' '}
                      <br />
                      {formData?.Coloader ? `CO-LOADER: ${formData.Coloader}` : ''}
                    </div>
                  </div>
                </div>
              </div>

              {/* Row 2: Consignee / Forwarding Agent */}
              <div className="grid grid-cols-12 border-t border-black">
                <div className="col-span-6 p-1 border-r border-black">
                  <div className="bol-value">CONSIGNEE (NOT NEGOTIABLE UNLESS CONSIGNED TO ORDER)</div>
                  <div className="text-xs whitespace-pre-line">{formData?.ConsigneeAddress}</div>
                </div>
                <div className="col-span-6">
                  <div className="bol-value p-1">FORWARDING AGENT - F.M.C No.</div>
                  <div className="p-1 text-xs whitespace-pre-line">
                    {formData?.ForwardingAgentAddress}
                  </div>
                </div>
              </div>

              {/* Row 3: Notify / Pre-carriage / Country of Origin */}
              <div className="grid grid-cols-12 border-t border-black">
                <div className="col-span-6 border-r border-black">
                  <div className="col-span-6">
                    <div className="bol-value p-1 pb-0">NOTIFY (COMPLETE NAME AND ADDRESS)</div>
                    <div className="p-1 pt-0 text-xs whitespace-pre-line">
                      {formData?.NotifyAddress ? formData.NotifyAddress : '\u00A0'}
                    </div>
                  </div>
                  <div className="col-span-6 border-t border-black grid grid-cols-2 gap-0 border-b">
                    <div className="border-r border-black p-1">
                      <div className="bol-value">PRE-CARRIAGE BY</div>
                      <div className="text-xs">{formData?.PreCarrageBy || ''}</div>
                    </div>
                    <div className="p-1">
                      <div className="bol-value">PLACE OF RECEIPT</div>
                      <div className="text-xs">{formData?.PalceOfReceipt || ''}</div>
                    </div>
                  </div>
                </div>
                <div className="col-span-6">
                  <div className="border-b border-black p-1">
                    <div className="bol-value">COUNTRY OF ORIGIN</div>
                    <div className="text-xs">{formData?.CountryOfOrigin || ''}</div>
                  </div>
                  <div className="p-1">
                    <div className="bol-value">FOR DELIVERY PLEASE CONTACT</div>
                    <div className="text-xs min-h-[40px] whitespace-pre-line">
                      {formData?.DeliveryAddress}
                      <div className="flex justify-end">
                        {((formData?.HBLPaymentMode && formData.HBLPaymentMode !== 'N') ||
                          formData?.HBLReleaseMode) ? (
                          <div className="flex flex-col items-end gap-0">
                            {formData?.HBLPaymentMode && formData.HBLPaymentMode !== 'N' && (
                              <div className="flex flex-row gap-1">
                                <span className="font-extrabold uppercase leading-none text-[13px] tracking-wide">FREIGHT</span>
                                <span className="font-extrabold uppercase leading-none text-[13px] tracking-wide">
                                  {formData.HBLPaymentMode === 'P' && 'PREPAID'}
                                  {formData.HBLPaymentMode === 'C' && 'COLLECT'}
                                </span>
                              </div>
                            )}
                            {formData?.HBLReleaseMode && (
                              <div className="flex flex-row gap-1">
                                <span className="font-extrabold uppercase leading-none text-[13px] tracking-wide">
                                  {formData.HBLReleaseMode === 'O' && 'ORIGINAL'}
                                  {formData.HBLReleaseMode === 'E' && 'EXPRESS'}
                                  {formData.HBLReleaseMode === 'R' && 'TELEX'}
                                  {formData.HBLReleaseMode === 'D' && 'RELEASE ORIGINALS AT DESTINATION'}
                                </span>
                                <span className="font-extrabold uppercase leading-none text-[13px] tracking-wide">RELEASE</span>
                              </div>
                            )}
                            {formData?.HBLPaymentMode === 'N' && (
                              <p className="text-xs font-semibold">{formData?.PaymentMode}</p>
                            )}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Row 4: Vessel / Voyage / Port of Load */}
              <div className="grid grid-cols-12 border-t border-black">
                <div className="col-span-4 p-1 border-r border-black">
                  <div className="bol-value">VESSEL / VOYAGE</div>
                  <div className="text-xs">
                    {formData?.VesselName || formData?.Voyage
                      ? `${formData?.VesselName || ''}${formData?.VesselName && formData?.Voyage ? ' / ' : ''}${formData?.Voyage || ''}`
                      : '\u00A0'}
                  </div>
                </div>
                <div className="col-span-4 p-1 border-r border-black">
                  <div className="bol-value">PORT OF LOADING</div>
                  <div className="text-xs">
                    {formData?.PortOfLoading?.split('[')[0].trim() || ' '}
                  </div>
                </div>
                <div className="col-span-4 p-1">
                  <div className="text-xs">
                    <div className="bol-value">LOADING PIER / TERMINAL</div>
                    {formData?.LoadingPierTerminal || '-'}
                  </div>
                </div>
              </div>

              {/* Row 5: Port of Discharge / Place of Delivery / Move Type */}
              <div className="grid grid-cols-12 border-t border-black">
                <div className="col-span-4 p-1 border-r border-black">
                  <div className="bol-value">PORT OF DISCHARGE</div>
                  <div className="text-xs">
                    {formData?.PortOfDischarge?.split('[')[0].trim() || ' '}
                  </div>
                </div>
                <div className="col-span-4 p-1 border-r border-black">
                  <div className="bol-value">PLACE OF DELIVERY</div>
                  <div className="text-xs">{formData?.PlaceOfDelivery || ''}</div>
                </div>
                <div className="col-span-4 p-1">
                  <div className="bol-value">MOVE TYPE</div>
                  <div className="text-xs">{formData?.MoveType || ''}</div>
                </div>
              </div>
            </div>

            {/* Cargo section */}
            <div className="p-1 bol-value text-center">PARTICULARS FURNISHED BY SHIPPER</div>
            <div className="border border-black">
              <div className="relative min-h-[100px]">
                <table className="hbl-cargo-table">
                  <colgroup>
                    <col style={{ width: '16.67%' }} />
                    <col style={{ width: '8.33%' }} />
                    <col style={{ width: '41.67%' }} />
                    <col style={{ width: '16.67%' }} />
                    <col style={{ width: '16.67%' }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th className="bol-value">MARKS & Nos</th>
                      <th className="bol-value">#PKGS</th>
                      <th className="bol-value">DESCRIPTION OF GOODS</th>
                      <th className="bol-value">GROSS WEIGHT</th>
                      <th className="bol-value">MEASUREMENT</th>
                    </tr>
                  </thead>
                  <tbody>
                    {formData?.ContainersCargoModel?.map((cargo, index) => (
                      <tr key={cargo.ID || index}>
                        <td className="text-xs">
                          {cargo.MARKS_NO}
                          <br />
                          {cargo.CONTAINER_NUMBER} / {cargo.SEAL_NUMBER}
                        </td>
                        <td className="text-xs text-center">
                          {cargo.CONT_SIZE} {cargo.CONT_TYPE}
                        </td>
                        <td className="text-xs whitespace-pre-line">
                          {cargo.DESCRIPTIONS}
                          <br />
                          {cargo.SCHEDULE_B && (
                            <>
                              <strong>Schedule B:</strong> {cargo.SCHEDULE_B}
                              <br />
                            </>
                          )}
                          {cargo.NO_PACKAGE > 0 && (
                            <div className="mt-2">
                              <strong>Packages:</strong> {cargo.NO_PACKAGE} {cargo.PACKAGE_CODE}
                              <br />
                              <strong>Package Desc:</strong> {cargo.PACKAGE_DESCRIPTION}
                              <br />
                            </div>
                          )}
                          {cargo.HAZ_CODE && (
                            <div className="mt-2">
                              <strong>HAZARDOUS:</strong> Yes
                              <br />
                              <strong>HAZ CODE:</strong> {cargo.HAZ_CODE}
                            </div>
                          )}
                        </td>
                        <td className="text-xs text-center">
                          {cargo.KGS ? `${cargo.KGS} KGS` : ''}
                          <br />
                          {cargo.LBS ? `${cargo.LBS} LBS` : ''}
                        </td>
                        <td className="text-xs text-center">
                          {cargo.CBM ? `${cargo.CBM} CBM` : ''}
                        </td>
                      </tr>
                    ))}

                    {(!formData?.ContainersCargoModel ||
                      formData.ContainersCargoModel.length === 0) && (
                      <tr style={{ minHeight: '280px' }}>
                        <td className="bol-value">&nbsp;</td>
                        <td className="bol-value">&nbsp;</td>
                        <td className="bol-value">&nbsp;</td>
                        <td className="bol-value">&nbsp;</td>
                        <td className="bol-value">&nbsp;</td>
                      </tr>
                    )}
                  </tbody>
                </table>

                <div
                  className="watermark absolute inset-0 flex items-center justify-center pointer-events-none"
                  style={{
                    color: 'hsl(220, 60%, 80%)',
                    fontSize: '64px',
                    fontWeight: 'bold',
                    letterSpacing: '6px',
                    transform: 'rotate(-25deg)',
                    opacity: 0.4,
                  }}
                >
                  DRAFT-COPY
                </div>

                {formData?.ReceiveShipment && company.CompanyId === 3 && (
                  <div
                    className="absolute pointer-events-none"
                    style={{ bottom: '12px', right: '24px', zIndex: 10 }}
                  >
                    <ReceivedStamp date={formData.ReceiveShipment} />
                  </div>
                )}
              </div>

              <div className="grid grid-cols-12 border-y border-black">
                <div className="col-span-7 p-1 border-r border-black text-[11px]">
                  <div>SHIPPERS DECLARED VALUE $</div>
                  <div>
                    SUBJECT TO EXTRA FRIGHT AS PER TARRIF AND TERMS AND CONDITIONS OF THIS B/L
                  </div>
                </div>
                <div className="col-span-5">
                  <div className="grid grid-cols-5">
                    <div className="col-span-3 border-r border-black">
                      <div className="bol-value p-1">FRIGHT PAYABLE AT</div>
                      <div className="text-xs text-center">{formData?.FreightPayable || '-'}</div>
                    </div>
                    <div className="col-span-2">
                      <div className="bol-value p-1">NO. OF ORIGINAL(S)</div>
                      <div className="text-xs text-center">{formData?.NoOriginal || '0'}</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-12 hbl-bottom-section">
                <div className="col-span-7 border-r border-black flex flex-col justify-between">
                  <table className="hbl-charges-table">
                    <thead>
                      <tr>
                        <th style={{ width: '50%' }}>DESCRIPTION OF CHARGES</th>
                        <th style={{ width: '25%' }}>PREPAID (USD)</th>
                        <th style={{ width: '25%' }}>COLLECT (USD)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {groupedCharges?.map((item, index) => (
                        <tr key={index}>
                          <td>{item.description}</td>
                          <td>{item.prepaid ? item.prepaid.toFixed(2) : ''}</td>
                          <td>{item.collect ? item.collect.toFixed(2) : ''}</td>
                        </tr>
                      ))}
                      {(!groupedCharges || groupedCharges.length === 0) && (
                        <tr>
                          <td colSpan={3} style={{ height: '60px' }}>&nbsp;</td>
                        </tr>
                      )}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td>GRAND TOTAL</td>
                        <td>
                          {groupedCharges
                            ?.reduce((sum, i) => sum + (i.prepaid || 0), 0)
                            .toFixed(2)}
                        </td>
                        <td>
                          {groupedCharges
                            ?.reduce((sum, i) => sum + (i.collect || 0), 0)
                            .toFixed(2)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>

                  <div className="grid grid-cols-2 border-t border-black">
                    <div className="p-1 flex items-center gap-1 border-r border-black">
                      <div className="bol-value">PLACE OF ISSUE:</div>
                      <span className="text-xs">
                        {formData?.PortOfLoading?.split('[')[0].trim() || ''}
                      </span>
                    </div>
                    <div className="p-1 flex items-center gap-1">
                      <div className="bol-value">DATE:</div>
                      <div className="text-xs">{formatDate(formData?.CreatedOn) || ''}</div>
                    </div>
                  </div>
                </div>

                <div className="col-span-5 flex flex-col justify-between hbl-signature-block">
                  <div
                    className="hbl-paragraph p-2 leading-tight"
                    style={{ minHeight: '70px' }}
                  >
                    <div className="text-[9px] leading-tight text-justify text-gray-800">
                      RECEIVED by the carrier the goods as specified above in apparent good order
                      and condition unless otherwise stated, to be transported to such place as
                      agreed, authorized or permitted herein and subject to all the terms and
                      conditions appearing on the front and reverse of this bill of lading to which
                      the Merchant agrees by accepting this bill of lading, and local privileges
                      and customs not withstanding. The particulars given above as stated by the
                      shipper and the weight, measure, quantity, condition, contents and value of
                      the goods are unknown to the Carrier. In WITNESS whereof one (1) original
                      bill of lading has been signed if not otherwise stated above, the same being
                      accomplished the other(s), if any to be void. If required by the carrier one
                      (1) original Bill of Lading must be surrendered duly endorsed in exchange for
                      the goods or delivery order.
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-xs underline text-gray-700">
                      Signed on behalf of the carrier
                    </div>
                    <div className="mb-10 text-md">{company.CompanyName || 'www.infox.com'}</div>
                    <div>
                      By
                      <span className="inline-block border-b border-black w-48 ml-2" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* FOOTER REGION (repeats on every page) */}
          <div
            ref={footerRef}
            className="hbl-print-footer-disclaimer hbl-footer-row"
            style={{ marginTop: '4px' }}
          >
            <div className="text-[8px] text-muted-foreground">
              Powered by {company.PoweredBy || 'www.infox.com'}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default HBLPrint;